import format from "date-fns/format";
import { procedure } from "./initialState";
import { DEFAULT_COUNTRY } from "../../../../utilities/staticConfigs";
import { getStorage } from "../../../../utilities/browserStorage";
import i18n from "../../../../utilities/i18n";
import { DEFAULT_CLAIM_ACCIDENT_TYPE, PRIORITIES } from "../../../../utilities/dictionaryConstants";
import { findDuplicates } from "./claimAlertValidations";
import { decimalConverter, formatDateUS } from "../../../../utilities/commonUtilities";


/**
 * Helper function to recursively replace empty strings ("") with null
 */
const nullifyEmptyStrings = (obj) => {
    if (obj === null || obj === undefined) return obj;
    
    if (Array.isArray(obj)) {
        return obj.map(item => (typeof item === 'object' ? nullifyEmptyStrings(item) : item));
    }
    
    if (typeof obj === 'object') {
        const cleanedObj = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value === "") {
                cleanedObj[key] = null;
            } else if (typeof value === 'object') {
                cleanedObj[key] = nullifyEmptyStrings(value);
            } else {
                cleanedObj[key] = value;
            }
        }
        return cleanedObj;
    }
    
    return obj;
};

export const createSaveData = (
    professionalClaimDetails,
    billingInfoDetails,
    serviceLineDetails,
    additionalClaimDetails,
    pks
) => {
    try {

        const isClaimEmpty = Object.keys(professionalClaimDetails || {}).length === 0;
        const isBillingEmpty = Object.keys(billingInfoDetails || {}).length === 0;
        const isServiceEmpty = Object.keys(serviceLineDetails || {}).length === 0;
        const isAdditionalEmpty = Object.keys(additionalClaimDetails || {}).length === 0;

        let diagnosisdata = {};
        let procedureArray = [];
        /**
         * structuring parent component that is professional claim details
         */
        let professionaldata = !isClaimEmpty ? professionalClaimDetails : {};
        let additionaldata = {};

        /********************************************************************** */
        /**
         * billing info details structure done here
         */
        let billingData = !isBillingEmpty ? billingInfoDetails : {};
        if (!isBillingEmpty) {
            let { payer_info, insurancePrimary, insurancesecondary, insuranceTertiary } = billingData;

            const payer_list = generatePayerInfo(payer_info, insurancePrimary, insurancesecondary, insuranceTertiary)

            if (payer_list.length > 0) {
                billingData = {
                    ...billingData,
                    pk: pks.billing_info_pk,
                    payer_info: payer_list
                };
            }

            billingData = {
                ...billingData,
                pk: pks.billing_info_pk,
            };

            delete billingData.primaryPayerId;
            delete billingData.secondaryPayerId;
            delete billingData.tertiaryPayerId;
        }
        if (!isServiceEmpty) {
            /****************************************************************** */
            /**
             * service line diagnosis value handled here
             */
            const {
                id,
                icd,
                diagnosis1,
                diagnosis2,
                diagnosis3,
                diagnosis4,
                diagnosis5,
                diagnosis6,
                diagnosis7,
                diagnosis8,
                diagnosis9,
                diagnosis10,
                diagnosis11,
                diagnosis12,
            } = serviceLineDetails;
            const diagnosisesDataVariables = {
                id,
                icd,
                diagnosis1,
                diagnosis2,
                diagnosis3,
                diagnosis4,
                diagnosis5,
                diagnosis6,
                diagnosis7,
                diagnosis8,
                diagnosis9,
                diagnosis10,
                diagnosis11,
                diagnosis12,
            };

            if (Object.keys(diagnosisesDataVariables).length !== 0) {
                diagnosisdata = {
                    ...diagnosisesDataVariables,
                    pk: pks.diagnosises_pk,
                };
            }
            /*************************************************************************/
            /**
             * procedure structure making
             */

            const newData = serviceLineDetails?.procedures ?? [];
            // dosFrom and dosTo is set for updating claim dos while saving,
            // if one procedure is added but any value not updated against it. so that
            // procedure will not save to db. for those case we need to iterate newArray
            // for final claim dos from and to
            if (serviceLineDetails?.procedures?.length === 1) {
                let firstItem = JSON.stringify(serviceLineDetails?.procedures[0]);
                let tempProcedure = JSON.stringify(procedure);
                if (firstItem === tempProcedure) {
                    procedureArray = [];
                }
            }
            newData.forEach((element) => {
                let item = JSON.parse(JSON.stringify(element));

                if (typeof (item?.id) == 'string' && item?.id?.startsWith("New-"))
                    item.id = ""

                if (
                    item.id ||
                    item.dos_from ||
                    item.dos_to ||
                    item.pos_id ||
                    item.cpt_hcpcs ||
                    item.charges
                ) {
                    if (item.dos_from)
                        item.dos_from = format(
                            new Date(
                                item.dos_from && typeof item.dos_from === "string"
                                    ? item.dos_from + " 00:00:00"
                                    : item.dos_from
                            ),
                            "yyyy-MM-dd"
                        );
                    if (item.dos_to)
                        item.dos_to = format(
                            new Date(
                                item.dos_to && typeof item.dos_to === "string"
                                    ? item.dos_to + " 00:00:00"
                                    : item.dos_to
                            ),
                            "yyyy-MM-dd"
                        );
                    if (!item.drug_code) {
                        item.drug_code = "";
                    }
                    for (const [key, value] of Object.entries(item)) {
                        // 2. Prevent deleting 'tos_id' if it's intentionally null or 0
                        if (value === "" && key !== "dx_pointers") { 
                            delete item[key];
                        }
                        if (key.endsWith("List")) {
                            delete item[key];
                        }
                        if (key.match('payment_done')) {
                            delete item[key];
                        }
                    }
                    procedureArray.push(item);
                }
            });
        }

        if (!isAdditionalEmpty) {
            additionaldata = {
                ...additionalClaimDetails,
                pk: pks.additional_claim_info_pk,
            };


            const buildLocationKeys = (prefix) => {
                const locData = {};
                // Determine the source key in patientInfo (e.g., 'guarantor_location_data' or 'location_data')
                const sourcePrefix = prefix ? `${prefix}_location_data` : 'location_data';

                // Determine the output prefix for your flat keys (e.g., 'guarantor_location')
                const outputPrefix = prefix ? `${prefix}_location` : 'ocation';

                if (additionalClaimDetails?.[sourcePrefix]) {
                    ['street1', 'street2', 'zip_code', 'city', 'state', 'country'].forEach(field => {
                        if (additionalClaimDetails[sourcePrefix][field] !== undefined) {
                            // Dynamically create flat keys like 'guarantor_location_city'
                            locData[`${outputPrefix}_${field}`] = additionalClaimDetails[sourcePrefix][field];
                        }
                    });
                }

                // Returns the flat object if it has data, otherwise undefined
                return Object.keys(locData).length > 0 ? locData : undefined;
            };

            const dropoffLoc = buildLocationKeys('dropoff');
            const pickupLoc = buildLocationKeys('pickup');

            if (dropoffLoc) {
                Object.assign(additionaldata, dropoffLoc);
            }

            if (pickupLoc) {
                Object.assign(additionaldata, pickupLoc);
            }

            if (Object.keys(additionalClaimDetails?.condition_indicator || {}).length !== 0) {
                Object.assign(additionaldata, {
                    ...additionalClaimDetails?.condition_indicator,
                });
            }

            const keysToDelete = ['pickup_location_data', 'dropoff_location_data', 'condition_indicator'];
            keysToDelete.forEach(key => {
                if (key in additionaldata) {
                    delete additionaldata[key];
                }
            });

        }

        /*******************************************************************************/

        return {
            professionaldata: nullifyEmptyStrings(professionaldata),
            billingData: nullifyEmptyStrings(billingData),
            diagnosisdata: nullifyEmptyStrings(diagnosisdata),
            procedureArray: nullifyEmptyStrings(procedureArray),
            additionaldata: nullifyEmptyStrings(additionaldata),
        };
    } catch (error) {
        console.error(error)
    }
};


/**
 * function is used to generate data that needed for billing info tab
 * @param {*} data
 */
export const generateBillingTabSaveData = (data, payload) => {
    let billingTabData = {};
    let payer_info_list = data.payer_info_list ?? [];
    let insuranceCompleted = false;
    const payerInfoData = makePayerInsuranceData(payer_info_list, payload);
    if (data.service_location.length > 0) {
        if (data.service_location[0].location_type === "practice") {
            billingTabData = {
                ...data,
                service_location: {
                    id: data.service_location[0].Id ?? null,
                    location_type: "practice",
                },
                service_location_value: `${data.service_location[0].Id}||practice`,
                location_type: "practice",
                payer_info: [],
                id: null,
                patient_signature: 1,
                insured_signature: 1,
                claim_fequency: 1,
                billing_provider: data.rendering_provider ?? null,
                accept_assignments: data.accept_assignemnt
                    ? data.accept_assignemnt
                    : null,
                ...payerInfoData,
            };
        } else if (data.service_location[0].location_type === "facility") {
            billingTabData = {
                ...data,
                service_location: {
                    id: data.service_location[0].Id ?? null,
                    location_type: "facility",
                },
                service_location_value: `${data.service_location[0].Id}||facility`,
                payer_info: [],
                id: null,
                patient_signature: 1,
                insured_signature: 1,
                claim_fequency: 1,
                billing_provider: data.rendering_provider,
                accept_assignments: data.accept_assignemnt
                    ? data.accept_assignemnt
                    : null,
                ...payerInfoData,
            };
        }
    } else {
        billingTabData = {
            ...data,
            payer_info: [],
            id: null,
            patient_signature: 1,
            insured_signature: 1,
            claim_fequency: 1,
            ...payerInfoData,
            billing_provider: data.rendering_provider ?? null,
            accept_assignments: data.accept_assignemnt
                ? data.accept_assignemnt
                : null,
        };
    }
    insuranceCompleted = true;
    return { billingTabData, insuranceCompleted };
};

/**
 * function to process and make data needed for service line tab
 * @param {*} data
 */

export const generateServiceLineSaveData = (data) => {
    let dx_pointers = "";
    const dxMap = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
    for (let index = 0; index < 12; index++) {
        const dx = data["diagnosis" + (index + 1)];
        if (dx) {
            dx_pointers += dxMap[index];
        }
    }
    let newArray = [];
    const propertiesToAssign = [
        "id",
        "dos_from",
        "dos_to",
        "pos_id",
        "pos_id_List",
        "tos_id",
        "tos_id_List",
        "emg",
        "cpt_hcpcs",
        "cpt_hcpcsList",
        "m1_id",
        "m1List",
        "m2_id",
        "m2List",
        "m3_id",
        "m3List",
        "m4_id",
        "m4List",
        "dx_pointers",
        "fee",
        "fee_units",
        "charges",
        "cpt_epsdt",
        "responsibility_type",
        "proced_description",
        "line_note",
        "drug_code",
        "drug_unit_type",
        "drug_unit_value",
        "measurement_unit_type",
        "measurement_unit_value",
        "anesthesia",
        "responsibility_type",
        "payment_doneList",
    ];
    // set the default cpts and other variable
    for (let index = 0; index < 12; index++) {
        if (!data["cpt" + (index + 1)]) continue;
        newArray[index] = {};
        propertiesToAssign.forEach((property) => {
            newArray[index][property] = "";
        });
        newArray[index]["cpt_hcpcs"] = data["cpt" + (index + 1)];
        newArray[index]["cpt_hcpcsList"] = [
            {
                id: data["cpt" + (index + 1)],
                name: data["cpt" + (index + 1) + "_name"],
            },
        ];
        for (let j = 1; j <= 4; j++) {
            if (
                data["cpt" + (index + 1) + "_m" + j + "_id"] &&
                data["cpt" + (index + 1) + "_m" + j + "_value"].length > 0
            ) {
                newArray[index]["m" + j + "_id"] =
                    data["cpt" + (index + 1) + "_m" + j + "_id"];
                newArray[index]["m" + j + "List"] =
                    data["cpt" + (index + 1) + "_m" + j + "_value"];
            }
        }
        if (data["pos" + (index + 1) + "_id"] == null) {
            if (data["location_pos_id"]) {
                newArray[index]["pos_id"] = data["location_pos_id"];
                newArray[index]["pos_id_List"] = data["location_pos_List"];
            } else {
                newArray[index]["pos_id"] = [];
                newArray[index]["pos_id_List"] = [];
            }
        } else {
            newArray[index]["pos_id"] = data["pos" + (index + 1) + "_id"];
            newArray[index]["pos_id_List"] = data["pos" + (index + 1) + "_id"]
                ? data["pos" + (index + 1) + "_List"]
                : [];
        }
        newArray[index]["tos_id"] = data["tos" + (index + 1) + "_id"];
        newArray[index]["tos_id_List"] = data["tos" + (index + 1) + "_id"]
            ? data["tos" + (index + 1) + "_List"]
            : [];
        newArray[index]["fee_units"] = data["default_unit" + (index + 1)];
        newArray[index]["dx_pointers"] = dx_pointers;
        newArray[index]["cpt_epsdt"] = 2;
        newArray[index]["emg"] = 2;
        newArray[index]["fee"] = data["default_fee" + (index + 1)];
        newArray[index]["responsibility_type"] = data["responsibility_type"];
        newArray[index]["charges"] =
            parseFloat(data["default_unit" + (index + 1)]) *
            parseFloat(data["default_fee" + (index + 1)]);
        newArray[index]["claim_status"] = data["claim_status"];
        newArray[index]["claim_sub_status"] = data["claim_sub_status"];
        newArray[index]['payment_done'] = false;
        newArray[index]["insurance_message"] = data["insurance_message"] ? data["insurance_message"] : "";
        newArray[index]["id"] = `proc-${crypto.randomUUID()}`
    }

    /**
     * creaating diagnosis list format here
     * @param {*} num
     * @returns
     */
    const createDiagnosisList = (num) => {
        const diagnosis = `diagnosis${num}`;
        const diagnosisValue = `${diagnosis}_value`;
        return {
            [diagnosis]: data[diagnosis] ? data[diagnosis].toString() : "",
            [`${diagnosis}List`]: data[diagnosis]
                ? [{ label: data[diagnosis], name: data[diagnosisValue] }]
                : [],
        };
    };
    /**
     * storing diagnosis value and list
     */
    let diagnosisdata;
    for (let i = 1; i <= 12; i++) {
        diagnosisdata = {
            ...diagnosisdata,
            ...createDiagnosisList(i),
        };
    }
    let serviceLineData = {
        icd: data.icd ? data.icd : 2,
        ...diagnosisdata,
        procedures: (newArray && Array.isArray(newArray) && newArray.length > 0) ? newArray.map((item) => ({ ...item, id: `New-${crypto.randomUUID()}` })) : []
    };

    return serviceLineData;
};
/**
 * additional data structure is build here
 * @param {*} data
 * @param {*} practiceData
 * @param {*} accidentList
 */

export const generateAdditionalclaimSaveData = (
    data,
    practiceData,
    accidentList
) => {
    const additionalData = {
        ...data,
        clia_number: practiceData.clia,
        accident: data.accident_type
            ? data.accident_type
            : accidentList?.find(
                (item) => item.name === DEFAULT_CLAIM_ACCIDENT_TYPE
            )?.id || null,
        state: data.accident_location ?? null,
        dropoff_location: data.dropoff_location ? data.dropoff_location : null,
        dropoff_location_data: data.dropoff_location_data 
            ? {
                street1: data.dropoff_location_data.street1 ?? null,
                street2: data.dropoff_location_data.street2 ?? null,
                city: data.dropoff_location_data.city ?? null,
                state: data.dropoff_location_data.state ?? null,
                country: data.dropoff_location_data.country ?? null,
                zip_code: data.dropoff_location_data.zip_code ?? null,
            }
            : {
                street1: null,
                street2: null,
                city: null,
                state: null,
                country: DEFAULT_COUNTRY,
                zip_code: null,
            },
        condition_indicator: {
            ambulance_service_medical_necessary:
                data.ambulance_service_medical_necessary
                    ? data.ambulance_service_medical_necessary
                    : false,
            patient_admitted_hospital: data.patient_admitted_hospital
                ? data.patient_admitted_hospital
                : false,
            patient_confined_bed_chair: data.patient_confined_bed_chair
                ? data.patient_confined_bed_chair
                : false,
            patient_moved_by_stretcher: data.patient_moved_by_stretcher
                ? data.patient_moved_by_stretcher
                : false,
            patient_physically_restrained: data.patient_physically_restrained
                ? data.patient_physically_restrained
                : false,
            patient_transported_emergency: data.patient_transported_emergency
                ? data.patient_transported_emergency
                : false,
            patient_unconscious_shock: data.patient_unconscious_shock
                ? data.patient_unconscious_shock
                : false,
            patient_visible_haemorrhaging: data.patient_visible_haemorrhaging
                ? data.patient_visible_haemorrhaging
                : false,
        },
        pickup_location: data.pickup_location ? data.pickup_location : "",
        pickup_location_data: data.pickup_location_data
            ? {
                street1: data.pickup_location_data.street1,
                street2: data.pickup_location_data.street2,
                city: data.pickup_location_data.city,
                state: data.pickup_location_data.state,
                country: data.pickup_location_data.country,
                zip_code: data.pickup_location_data.zip_code,
            }
            : {
                street1: null,
                street2: null,
                city: null,
                state: null,
                country: DEFAULT_COUNTRY,
                zip_code: null,
            },
        employment: data.employment ? data.employment : 2,
        ambulance_claim: data.ambulance_claim ? data.ambulance_claim : 2,
    };

    return additionalData;
};

/**
 * parent component data structure is build here during existing claim update
 * @param {*} data
 */
export const generateClaimCommonUpdateData = (claim_info) => {
    let commonData = {
        ...claim_info,
        locked_by_name:
            claim_info?.locked &&
                claim_info?.locked_by ===
                JSON.parse(getStorage("userData"))?.profile_id
                ? i18n.t("commons.self")
                : claim_info?.locked_by_name,
        claim_editable:
            !claim_info?.locked ||
                (claim_info?.locked &&
                    claim_info?.locked_by ===
                    JSON.parse(getStorage("userData"))?.profile_id &&
                    claim_info?.unid === JSON.parse(getStorage("userData"))?.unid)
                ? true
                : false,
        claim_inactive: claim_info?.active ? false : true,
        active: claim_info?.active,
        skip_auto_ev: !!claim_info?.skip_auto_ev,
        claim_locked:
            claim_info?.locked &&
                claim_info?.locked_by !==
                JSON.parse(getStorage("userData"))?.profile_id &&
                claim_info?.unid !== JSON.parse(getStorage("userData"))?.unid
                ? true
                : false,
    };

    return commonData;
};

/**
 * fucntion to find Pid from data list
 * @param {function to find} insuranceList 
 * @param {*} policyId 
 * @returns 
 */
export const findPId = (insuranceList, policyId, payerInfo) => {
    const targetPayer = payerInfo.find(p => p.policy_id === policyId);
    const match = insuranceList.find(item =>
        item.policy_id === policyId
        &&
        item.name === targetPayer.name
    );
    return match ? match.pId : null;
};

/**
 * billing info data structure is build here during existing claim update
 * @param {*} data
 */
export const generateBillingInfoUpdateData = (data, payload) => {
    let billingInfoTabData = {};
    let payer_info_list = data.payer_info_list ?? [];
    let payer_info = data.payer_info ?? []
    let insuranceCompleted = false;
    const payerInfoData = makePayerInsuranceData(payer_info_list, payload, payer_info);
    if (data?.facility_location === "") {
        billingInfoTabData = {
            ...data,
            payer_info: [],
            service_location: {
                id: data?.practice_location,
                location_type: "practice",
            },
            service_location_value: `${data?.practice_location}||practice`,
        };
    } else if (data?.practice_location === "") {
        billingInfoTabData = {
            ...data,
            payer_info: [],
            service_location: {
                id: data?.facility_location,
                location_type: "facility",
            },
            service_location_value: `${data?.facility_location}||facility`,
        };
    }
    insuranceCompleted = true;
    return { billingInfoTabData, payerInfoData, insuranceCompleted };
};

export const generateServiceLineUpdateData = (procedureData) => {
    const createDiagnosisList = (num) => {
        const diagnosis = `diagnosis${num}`;
        const diagnosisValue = `${diagnosis}_value`;
        return {
            [diagnosis]: procedureData[diagnosis]
                ? procedureData[diagnosis].toString()
                : "",
            [`${diagnosis}List`]: procedureData[diagnosis]
                ? [
                    {
                        label: procedureData[diagnosis],
                        name: procedureData[diagnosisValue],
                    },
                ]
                : [],
        };
    };
    /**
     * storing diagnosis value and list
     */
    let diagnosisdata;
    for (let i = 1; i <= 12; i++) {
        diagnosisdata = {
            ...diagnosisdata,
            ...createDiagnosisList(i),
        };
    }

    /**
     * making procdure line structure
     */
    let newData = [];
    if (procedureData?.procedures.length > 0) {
        procedureData?.procedures?.forEach((item) => {
            let data = {
                id: item.id,
                dos_from: item.dos_from ? item.dos_from : "",
                dos_to: item.dos_to ? item.dos_to : "",
                pos_id: item.pos,
                pos_id_List: [item.pos ? { id: item.pos, name: item.pos_drop_down_name, label: item.pos_drop_down_name } : ""],
                tos_id: item.tos,
                tos_id_List: [item.tos ? { id: item.tos, name: item.tos_drop_down_name, label: item.tos_drop_down_name } : ""],
                emg: item.emg,
                cpt_hcpcs: item.cpt_hcpcs,
                cpt_hcpcsList: item.cpt_hcpcs ? [{ "id": item.cpt_hcpcs, "name": item.cpt_value }] : [],
                m1_id: item.m1 ? item.m1 : [],
                m2_id: item.m2 ? item.m2 : [],
                m3_id: item.m3 ? item.m3 : [],
                m4_id: item.m4 ? item.m4 : [],
                m1List: item.m1 ? [{ id: item.m1, name: item.m1_value }] : [],
                m2List: item.m2 ? [{ id: item.m2, name: item.m2_value }] : [],
                m3List: item.m3 ? [{ id: item.m3, name: item.m3_value }] : [],
                m4List: item.m4 ? [{ id: item.m4, name: item.m4_value }] : [],
                dx_pointers: item.dx_pointers,
                fee: item.fee ? decimalConverter(item.fee, 2) : 0.00,
                fee_units: item.fee_units,
                charges: item.charges,
                cpt_epsdt: item.cpt_epsdt,
                proced_description: item.proced_description,
                line_note: item.line_note,
                drug_code: item.drug_code ? item.drug_code : "", // converted to number
                drug_unit_type: item.drug_unit_type,
                drug_unit_value: item.drug_unit_value,
                measurement_unit_type: item.measurement_unit_type,
                measurement_unit_value: item.measurement_unit_value,
                anesthesia: item.anesthesia,
                responsibility_type: item.responsibility_type,
                payment_done: item.payment_done == true ? true : false,
                balance: item.balance ? Number(item.balance) : "",
                claim_status: item.claim_status ?? "",
                claim_sub_status: item.claim_sub_status ?? "",
                insurance_message: item.insurance_message ?? "",
            }
            newData.push(data)
        })
    }
    const procedurDetails = {
        icd: procedureData.icd ? procedureData.icd : 2,
        ...diagnosisdata,
        procedures: newData,
    };

    return procedurDetails;
};

export const generateAdditionaleUpdateData = (additionalInfo, accidentList) => {
    let additonalData = {
        ...additionalInfo,
        accident: additionalInfo.accident
            ? additionalInfo.accident
            : accidentList?.find(
                (item) => item.name === DEFAULT_CLAIM_ACCIDENT_TYPE
            )?.id || "",
        condition_indicator: {
            ambulance_service_medical_necessary: additionalInfo.ambulance_service_medical_necessary ? additionalInfo.ambulance_service_medical_necessary : false,
            patient_admitted_hospital: additionalInfo.patient_admitted_hospital ? additionalInfo.patient_admitted_hospital : false,
            patient_confined_bed_chair: additionalInfo.patient_confined_bed_chair ? additionalInfo.patient_confined_bed_chair : false,
            patient_moved_by_stretcher: additionalInfo.patient_moved_by_stretcher ? additionalInfo.patient_moved_by_stretcher : false,
            patient_physically_restrained: additionalInfo.patient_physically_restrained ? additionalInfo.patient_physically_restrained : false,
            patient_transported_emergency: additionalInfo.patient_transported_emergency ? additionalInfo.patient_transported_emergency : false,
            patient_unconscious_shock: additionalInfo.patient_unconscious_shock ? additionalInfo.patient_unconscious_shock : false,
            patient_visible_haemorrhaging: additionalInfo.patient_visible_haemorrhaging ? additionalInfo.patient_visible_haemorrhaging : false,
        },
        dropoff_location_data: additionalInfo.dropoff_location_data ? {
            "street1": additionalInfo.dropoff_location_data.street1, "street2": additionalInfo.dropoff_location_data.street2,
            "city": additionalInfo.dropoff_location_data.city, "state": additionalInfo.dropoff_location_data.state,
            "country": additionalInfo.dropoff_location_data.country,
            "zip_code": additionalInfo.dropoff_location_data.zip_code
        }
            : {
                "street1": "", "street2": "", "city": "", "state": "", "country": DEFAULT_COUNTRY, "zip_code": ""
            },
        dropoff_location: additionalInfo.dropoff_location ? additionalInfo.dropoff_location : "",
        pickup_location: additionalInfo.pickup_location ? additionalInfo.pickup_location : "",
        pickup_location_data: additionalInfo.pickup_location_data ? {
            "street1": additionalInfo.pickup_location_data.street1, "street2": additionalInfo.pickup_location_data.street2,
            "city": additionalInfo.pickup_location_data.city, "state": additionalInfo.pickup_location_data.state,
            "country": additionalInfo.pickup_location_data.country,
            "zip_code": additionalInfo.pickup_location_data.zip_code
        } : {
            "street1": "", "street2": "", "city": "", "state": "", "country": DEFAULT_COUNTRY, "zip_code": ""
        },
    }
    return additonalData;
}

export const getServiceDateFrom = (date, procedure, rowId) => {
    let serviceDateFrom;
    procedure?.forEach((element, index) => {
        let procedureDosFrom = element.dos_from ? element.dos_from : "";
        if (index !== rowId) {
            if (
                procedureDosFrom &&
                new Date(procedureDosFrom) < new Date(date)
            ) {
                serviceDateFrom = procedureDosFrom;
            }
        }
    });

    return serviceDateFrom ? serviceDateFrom : date;
};

export const getServiceDateTo = (date, procedure, rowId) => {
    let serviceDateTo;
    procedure?.forEach((element, index) => {
        let procedureDosTo = element.dos_to ? element.dos_to : "";
        if (index !== rowId) {
            if (procedureDosTo && new Date(procedureDosTo) > new Date(date)) {
                serviceDateTo = procedureDosTo;
            }
        }
    });

    return serviceDateTo ? serviceDateTo : date;
};

export const validateModifier = (procedures) => {
    let modifiervalidate = [];
    let modifier1 = [];
    let modifier2 = [];
    let modifier3 = [];
    let modifier4 = [];

    procedures.forEach((item, index) => {
        let M1 = validateModifierField(item.m1_id);
        let M2 = validateModifierField(item.m2_id);
        let M3 = validateModifierField(item.m3_id);
        let M4 = validateModifierField(item.m4_id);

        modifier1[index] = modifier2[index] = modifier3[index] = modifier4[index] = true;
        modifiervalidate[index] = true;

        if (M4) {
            modifier1[index] = M1;
            modifier2[index] = M2;
            modifier3[index] = M3;
            if (!M1 || !M2 || !M3) {
                modifiervalidate[index] = false;
            }
        } else if (M3) {
            modifier1[index] = M1;
            modifier2[index] = M2;
            modifier3[index] = true;
            if (!M1 || !M2) {
                modifiervalidate[index] = false;
            }
        } else if (M2) {
            modifier1[index] = M1;
            modifier2[index] = true;
            if (!M1) {
                modifiervalidate[index] = false;
            }
        }
    });

    return {
        modifier1,
        modifier2,
        modifier3,
        modifier4,
        modifiervalidate,
    };
};

const validateModifierField = (field) => Array.isArray(field) ? field.length > 0 : !!field;

export const validateDiagnosis = (serviceData, validationState) => {
    let dgnValid = validationState;
    let tmpName = "";
    let foundTopOrderValue = false;
    let isValidted = true;
    let isDuplicate = false;
    let codeList = [];
    for (let dgCnt = 12; dgCnt > 0; dgCnt--) {
        tmpName = "diagnosis" + String(dgCnt);
        if (serviceData[tmpName]) {
            foundTopOrderValue = true;
            dgnValid[tmpName] = "";
            codeList.push(parseInt(serviceData[tmpName]));
        } else if (foundTopOrderValue && !serviceData[tmpName]) {
            dgnValid[tmpName] = " input-error";
            isValidted = false;
        } else if (!foundTopOrderValue) {
            dgnValid[tmpName] = "";
        }
    }
    let duplicateList = findDuplicates(codeList);
    if (isValidted && duplicateList.length > 0) {
        isDuplicate = true;
        isValidted = false;
    }

    return {
        dgnValid: dgnValid,
        isDuplicate: isDuplicate,
        isValidted: isValidted,
    };
};


export const claimFormWaringMessage = (data) => {
    let warningText = "";
    if (data.claim_validations &&
        data.claim_validations.length > 0) {
        data.claim_validations.map((item) => {
            let lbl = Array.isArray(item) ? item[0] : item;
            let l_text = "<div><span style='width: 40px; display: inline-block; text-align: right;'>" + (lbl) + ".&nbsp;&nbsp;</span><span>" + i18n.t("warning.claim." + lbl) + "</div>";
            if (Array.isArray(item)) {
                l_text = l_text.replace("{" + Object.keys(item[1])[0] + "}", Object.values(item[1])[0]);
            }
            warningText = warningText + l_text
        })
    }

    return warningText;
}


export const setWarningFromServer = (response) => {
    let warningsArray = [];
    warningsArray = warningsArray.filter(function (obj) {
        return obj.type !== 'validation';
    });
    if (response?.claim_validations && response?.claim_validations?.length > 0) {
        let claimValidations = response?.claim_validations;
        claimValidations.forEach((item) => {
            let warningExist = warningsArray.filter((warningObj) => warningObj.tabName === item);
            let warningCode = '';
            let warningMessages = '';
            if (Array.isArray(item)) {
                warningCode = item[0];
                let optionString = i18n.t('warning.claim.' + warningCode);
                warningMessages = optionString.replace(/{(\w+)}/g, function (_, k) {
                    return item[1][k];
                });
            } else {
                warningCode = item;
                warningMessages = i18n.t('warning.claim.' + warningCode);
            }

            let warnings = { "tabName": warningCode, "type": "validation" }
            warnings[warningMessages] = "";
            if (warningExist) {
                let warningIndexItem = '';
                if (Array.isArray(item))
                    warningIndexItem = item[0];
                else
                    warningIndexItem = item;
                let warningIndex = warningsArray.findIndex(items => items.tabName === warningIndexItem);
                if (warningIndex < 0) {
                    warningsArray.push(warnings);
                } else {
                    warningsArray[warningIndex] = warnings;
                }
            } else {
                warningsArray.push(warnings);
            }
        })
    }

    return warningsArray;
}


/**
 * @description Util function to validate if any empty slots exists b/w icds on ICD Handle Change reducer
 * @param {*} slotValues 
 * @returns Boolean
 */
export const checkForGapsBetweenIcd = (slotValues) => {
    const keys = Object.keys(slotValues);
    let lastFilledIndex = -1;
    let emptyFoundAfterFilled = false;

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (i === 0 && slotValues[key] === "") {
            // Check if any non-empty values exist after the first key
            return keys.slice(1).some(k => slotValues[k] !== "");
        }

        if (slotValues[key]) {
            if (emptyFoundAfterFilled) {
                return true;
            }
            lastFilledIndex = i;
        } else {
            if (lastFilledIndex !== -1) {
                emptyFoundAfterFilled = true;
            }
        }
    }
    return false;
}


/**
 * @description Util function to validate if user is pointing the DX pointers to the ICDs which are selected
 * @param {Object} serviceLineDetails Redux State
 * @param {string} requestDxPointers user input string of dx pointer
 * @returns Boolean
 */
export const validateIsValidDxPointer = (serviceLineDetails, requestDxPointers) => {
    let dxPointers = [...requestDxPointers];
    let isValidPointer = true;
    const dxPointerKeyMapping = {
        "A": "diagnosis1",
        "B": "diagnosis2",
        "C": "diagnosis3",
        "D": "diagnosis4",
        "E": "diagnosis5",
        "F": "diagnosis6",
        "G": "diagnosis7",
        "H": "diagnosis8",
        "I": "diagnosis9",
        "J": "diagnosis10",
        "K": "diagnosis11",
        "L": "diagnosis12"
    };

    dxPointers.forEach((item) => {
        if (!serviceLineDetails[dxPointerKeyMapping[item]]) {
            isValidPointer = false;
        }
    })
    return isValidPointer;
}


export const updateDXpointer = (serviceLineDetails) => {
    const dxPointerKeyMapping = {
        "A": "diagnosis1",
        "B": "diagnosis2",
        "C": "diagnosis3",
        "D": "diagnosis4",
        "E": "diagnosis5",
        "F": "diagnosis6",
        "G": "diagnosis7",
        "H": "diagnosis8",
        "I": "diagnosis9",
        "J": "diagnosis10",
        "K": "diagnosis11",
        "L": "diagnosis12"
    };

    let dxPointers = "";

    for (let key in dxPointerKeyMapping) {
        const diagnosisKey = dxPointerKeyMapping[key];
        if (serviceLineDetails[diagnosisKey]) {
            dxPointers += key;
        }
    }

    return dxPointers;
}
/**
 * creating payer info structure
 * @param {*} insuranceData 
 * @param {*} claimPK 
 */
export const makePayerInsuranceData = (insuranceData, claimPK, payer_info_db_data = []) => {
    let arrInsurePriority = [];
    let newarrInsurance = [];
    let InsuranceObject = {};
    let primaryInsList = [];
    let secondaryInsList = [];
    let tertiaryInsList = [];
    let claim_pk = !claimPK ? "add_new" : claimPK;
    let updateValue;
    if (Array.isArray(insuranceData) && insuranceData.length > 0) {
        let pId = `pId${crypto?.randomUUID()}`
        insuranceData.forEach((item) => {
            pId = pId + item.policy_id;
            let data = {
                id: item.id,
                payer_id: item.id,
                name: item.name,
                policy_id: item.policy_id,
                pId: pId,
                ins_payer_id: item.ins_payer_id
            }

            let priorityKey = "primaryPayerId";
            if (item.priority === 2) priorityKey = "secondaryPayerId";
            if (item.priority === 3) priorityKey = "tertiaryPayerId";


            const priorityObj = PRIORITIES.find(pIObj => pIObj.id === item.priority);
            const arryObj = {
                practice_insurance_id: item.id,
                policy_id: item.policy_id,
                [priorityKey]: item.ins_payer_id,
                pId: pId,

            };

            if (priorityObj) {
                arryObj.priority = priorityObj.id;
            }
            arrInsurePriority.push({ ...arryObj, pId: pId, Id: arryObj.practice_insurance_id });
            newarrInsurance.push({ ...arryObj, Id: arryObj.practice_insurance_id, pId: pId });
            if (item.priority === 1) {
                if (primaryInsList.length === 0) {
                    InsuranceObject = {
                        ...InsuranceObject,
                        insurancePrimary: pId,
                        primarypolicyId: item.policy_id,
                        primaryPayerId: item.ins_payer_id,
                        pId: pId
                    }
                }
                primaryInsList.push(data);
            }
            else if (item.priority === 2) {
                if (secondaryInsList.length === 0) {
                    InsuranceObject = {
                        ...InsuranceObject,
                        insurancesecondary: pId,
                        secondarypolicyId: item.policy_id,
                        secondaryPayerId: item.ins_payer_id,
                        pId: pId
                    }
                }
                secondaryInsList.push(data)
            } else {
                if (tertiaryInsList.length === 0) {
                    InsuranceObject = {
                        ...InsuranceObject,
                        insuranceTertiary: pId,
                        tertiarypolicyId: item.policy_id,
                        tertiaryPayerId: item.ins_payer_id,
                        pId: pId
                    }
                }
                tertiaryInsList.push(data);
            }

        })
    }


    if (arrInsurePriority.length > 0 && claim_pk == "add_new") {
        updateValue = {
            payer_info: [],
            payerInsuranceList: arrInsurePriority,
            ...InsuranceObject,
            primaryInsuranceDataList: primaryInsList.length > 0 ? primaryInsList : [],
            secondaryInsuranceDataList: secondaryInsList.length > 0 ? secondaryInsList : [],
            tertiaryInsuranceDataList: tertiaryInsList.length > 0 ? tertiaryInsList : [],
        }
    } else {
        updateValue = {
            payerInsuranceList: arrInsurePriority,
            payer_info: [],
            primaryInsuranceDataList: primaryInsList.length > 0 ? primaryInsList : [],
            secondaryInsuranceDataList: secondaryInsList.length > 0 ? secondaryInsList : [],
            tertiaryInsuranceDataList: tertiaryInsList.length > 0 ? tertiaryInsList : [],
        }
    }

    let payer_info = [];

    const {
        payerInsuranceList = [],
        insurancePrimary,
        insurancesecondary,
        insuranceTertiary
    } = updateValue || {};

    // CASE 1: Adding a new claim
    if (claim_pk === "add_new") {
        if (Array.isArray(payerInsuranceList) && payerInsuranceList.length > 0) {
            payer_info = payerInsuranceList.filter(ele =>
                (ele.priority === 1 && ele.pId === insurancePrimary) ||
                (ele.priority === 2 && ele.pId === insurancesecondary) ||
                (ele.priority === 3 && ele.pId === insuranceTertiary)
            );
        }
    }

    // CASE 2: Updating existing claim
    else {
        if (Array.isArray(payerInsuranceList) && payerInsuranceList.length > 0 && Array.isArray(payer_info_db_data)) {
            const updatedList = [];

            payerInsuranceList.forEach(ele => {
                payer_info_db_data.forEach(item => {
                    if (ele.policy_id === item.policy_id) {
                        updatedList.push({
                            practice_insurance_id: item.id,
                            policy_id: item.policy_id,
                            priority: item.priority,
                            Id: item.id,
                            pId: ele.pId
                        });
                    }
                });
            });

            payer_info = updatedList;
        }
    }

    updateValue = {
        ...updateValue,
        payer_info,
    };


    return updateValue;
}

const generatePayerInfo = (payer, primary_id, secondary_id, tertiary_id) => {
    let payer_list = [];
    if (Array.isArray(payer) && payer.length > 0) {
        const findPayerById = (id) => payer.find(item => item?.pId === id);
        const removePid = (obj) => {
            if (!obj) return null;
            const rest = { ...obj };
            delete rest.pId;
            return rest;
        };
        if (primary_id) {
            const primary = findPayerById(primary_id);
            if (primary) {
                payer_list.push(removePid(primary));
            }
        }

        if (secondary_id) {
            const secondary = findPayerById(secondary_id);
            if (secondary) {
                payer_list.push(removePid(secondary));
            }
        }

        if (tertiary_id) {
            const tertiary = findPayerById(tertiary_id);
            if (tertiary) {
                payer_list.push(removePid(tertiary));
            }
        }
    }

    return payer_list;
};

export const buildClaimFilters = (searchQueryInput, practicePK, patient_PK) => {
    let filters = {
        practice_pk: practicePK || null,
        patient_pk: patient_PK || null,
        billing_provider_pk: searchQueryInput.provider || null,
        claim_type: searchQueryInput.claimType || null,
        claim_status_pk: searchQueryInput.claimStatus || null,
        primary_payer_pk: searchQueryInput.primaryPayer || null,
        secondary_payer_pk: searchQueryInput.secondaryPayer || null,
        payer_type: searchQueryInput.payerType || null,
        cpt_hcps: searchQueryInput.selectedCptIDs || [],
        icd: searchQueryInput.icd || null,
        current_payer_pk: searchQueryInput.currentPayer || null,
    };

    if (searchQueryInput.dateOfServiceFrom) {
        filters.dos_from = formatDateUS(searchQueryInput.dateOfServiceFrom);
    }
    if (searchQueryInput.dateOfServiceTo) {
        filters.dos_to = formatDateUS(searchQueryInput.dateOfServiceTo);
    }
    if (searchQueryInput.claimEnteredFrom) {
        filters.claim_entered_from = formatDateUS(searchQueryInput.claimEnteredFrom);
    }
    if (searchQueryInput.claimEnteredTo) {
        filters.claim_entered_to = formatDateUS(searchQueryInput.claimEnteredTo);
    }
    if (searchQueryInput.claimSubmittedFrom) {
        filters.claim_submitted_from = formatDateUS(searchQueryInput.claimSubmittedFrom);
    }
    if (searchQueryInput.claimSubmittedTo) {
        filters.claim_submitted_to = formatDateUS(searchQueryInput.claimSubmittedTo);
    }
    if (searchQueryInput.selectServiceLocation) {
        filters.service_location = searchQueryInput.serviceLocation;
        filters.service_location_type = searchQueryInput.serviceLocationType;
    }
    if (searchQueryInput.claimType === 2) {
        filters.revenue_codes = searchQueryInput.revenueCodes;
    }
    return filters;
};
/**
 * function to create request data for fetch fee schedule data if exist
 * if we click refresh against each procedure it will store only details of that procedure to pass to backend
 * if clicking on refresh all button it will pass all procedure to backend
 * @param {*} billingInfoDetails 
 * @param {*} serviceLineDetails 
 * @param {*} billingInfoDetails 
 * @param {*} serviceLineDetails 
 * @param {*} additionalClaimDetails 
 */
export const fetchFeeRequestDataFormatter = (
    billingInfoDetails = {},
    serviceLineDetails = {},
    additionalClaimDetails = {},
    type = null,
    id = null
) => {
    try {
        const {
            billing_provider_id = null,
            rendering_provider_id = null,
            payer_info = [],
        } = billingInfoDetails;

        const {
            lab_sales_rep = null,
            clinic_site_name = null,
        } = additionalClaimDetails;

        const requestData = {
            static_details: {
                billing_provider: billing_provider_id && billing_provider_id != "" ? billing_provider_id : null,
                rendering_provider: rendering_provider_id && rendering_provider_id != "" ? rendering_provider_id : null,
                labrep: lab_sales_rep && lab_sales_rep !== "" ? lab_sales_rep : null,
                clinic_site: clinic_site_name && clinic_site_name !== "" ? clinic_site_name : null,
                insurance: null,
            },
            procedures: [],
        };
        // Set insurance with priority 1 (if available)
        const priorityOneInsurance = payer_info.find(
            (item) => item.priority === 1 && item.pId == billingInfoDetails?.insurancePrimary
        );
        if (priorityOneInsurance) {
            requestData.static_details.insurance =
                priorityOneInsurance.practice_insurance_id && priorityOneInsurance.practice_insurance_id != "" ?
                    priorityOneInsurance.practice_insurance_id : null;
        }

        //Prepare procedure data
        if (Array.isArray(serviceLineDetails.procedures)) {
            let proceduresToProcess = [];

            if (type === "single" && id) {
                const singleProcedure = serviceLineDetails.procedures.find(
                    (item) => String(item?.id) === String(id)
                );
                if (singleProcedure) {
                    proceduresToProcess = [singleProcedure];
                }
            } else {
                // Default to processing all procedures
                proceduresToProcess = serviceLineDetails.procedures;
            }

            requestData.procedures = proceduresToProcess.map((item) => ({
                procedure_id: item?.id && item?.id != "" ? item?.id : null,
                cpt_code: item?.cpt_hcpcs && item?.cpt_hcpcs != "" ? item?.cpt_hcpcs : null,
                service_date_from: item.dos_from && item.dos_from != "" ? item.dos_from : null,
                service_date_to: item.dos_to && item.dos_to != "" ? item.dos_to : null,
                modifier: item?.m1List && item.m1List.length > 0 ? item.m1_id : null
            }));
        }

        return requestData;
    } catch (error) {
        console.error(error)
    }
};

export const handleIdentifierErrors = (error) => {
    const data = error.data;
    const errorMap = {
        refering_provider_id: i18n.t("commons.refProviderReq"),
        billing_provider_id: i18n.t("commons.billingReq"),
    }
    // Find the key that exists in both the API response and our map
    const activeKey = Object.keys(data).find(key => errorMap[key]);

    return {
        message: activeKey ? errorMap[activeKey] : i18n.t("commons.unexpectedError")
    };
}




export const FieldPermissionGenerator = (permittedField) => {
    const charges = permittedField?.charges ?? {};

    const field_claiminfo = {
        ...charges.claim_basic_fields
    };

    const field_billinginfo = {
        ...charges.billing_info,
        ...charges.payer_info
    };

    const fieldServiceInfo = {
        ...charges.service_lines
    };

    const fieldAdditionalInfo = {
        ...charges.additional_claim_info
    };

    const fieldAddNotInfo = {
        ...charges.ar_notes
    }

    return {
        field_claiminfo,
        field_billinginfo,
        fieldServiceInfo,
        fieldAdditionalInfo,
        fieldAddNotInfo
    };
};

// All claim sections that should be diffed together on a single update click.
export const CLAIM_SECTION_KEYS = [
    'professionalClaimDetails',
    'billingInfoDetails',
    'serviceLineDetails',
    'additionalClaimDetails',
];

/**
 * Fields that should always be skipped when diffing current vs. original data.
 * These are matched by key name at any nesting depth.
 *
 * Add generic, cross-section noise here (e.g. UI-only flags, server audit
 * fields). For section-specific ignores, pass a per-section map to
 * `buildAllChangedFieldsPayload` instead.
 */
export const DEFAULT_IGNORED_FIELDS = [
    "permittedFields"
];

/**
 * Ignored fields scoped to a specific section. Keys match section keys in
 * `CLAIM_SECTION_KEYS`; values are arrays of field names (matched at any depth)
 * that should be ignored only while diffing that section.
 */
export const SECTION_IGNORED_FIELDS = {
    professionalClaimDetails: [
        "custom_claim_id",
        "patient_name",
        "dos_from",
        "charges",
        "balance",
        "last_billed_date",
        "identifierData",
        "responsible_payer_name",
        "work_status",
        "claim_stage_id",
        "id",
        "claim_type",
        "posted_date",
        "pks",
        "locked_by_name",
        "insurancePrimary",
        "payerInsuranceList",
        "primarypolicyId",
        "insuranceSecondary",
        "secondarypolicyId",
        "insuranceTertiary",
        "tertiarypolicyId",
    ],
    billingInfoDetails: [
        "service_location_value",
        "service_locations",
        "insurancePrimary",
        "payerInsuranceList",
        "primarypolicyId",
        "insuranceSecondary",
        "secondarypolicyId",
        "insuranceTertiary",
        "tertiarypolicyId",
        "insurancesecondary",
        "insuranceprimary",
        "insurancetertiary",
        "primaryPayerId",
        "secondaryPayerId",
        "tertiaryPayerId",
    ],
};

const toIgnoreSet = (ignoreFields) => {
    if (!ignoreFields) return null;
    if (ignoreFields instanceof Set) {
        return ignoreFields.size ? ignoreFields : null;
    }
    if (Array.isArray(ignoreFields) && ignoreFields.length) {
        return new Set(ignoreFields);
    }
    return null;
};

/**
 * Deep-diff between a current value and its original (duplicate) value.
 * Returns:
 *  - `undefined` when there is no change
 *  - the new value (or a partial object containing only changed keys) otherwise
 *
 * Behaviour notes:
 *  - Plain objects are diffed key by key, returning only the changed keys
 *    (preserving the original data shape for those keys).
 *  - Arrays are treated as atomic: if any element differs (or length differs),
 *    the entire new array is returned. This keeps payloads predictable when
 *    sending lists like `procedures`, `payer_info`, etc. to the backend.
 *    Ignored fields are still honoured for elements that are plain objects,
 *    so arrays-of-objects won't report a change caused solely by ignored keys.
 *  - `null` / `undefined` are treated as equivalent only when both sides are nullish.
 *  - `ignoreFields` (Set | Array of strings) skips keys by name at ANY depth.
 */
const getChangedFields = (current, original, ignoreFields) => {
    const ignoreSet = ignoreFields instanceof Set ? ignoreFields : toIgnoreSet(ignoreFields);

    if (current === original) return undefined;
    if (current == null && original == null) return undefined;
    if (current == null || original == null) return current;

    if (Array.isArray(current) || Array.isArray(original)) {
        if (!Array.isArray(current) || !Array.isArray(original)) return current;

        const changedElements = current.map((item, index) => {
            const originalItem = original[index];

            if (originalItem === undefined) return item;

            const itemDiff = getChangedFields(item, originalItem, ignoreSet);

            if (itemDiff !== undefined && typeof item === 'object' && item !== null) {
                return {
                    ...(item.id ? { id: item.id } : {}),
                    ...(item.pk ? { pk: item.pk } : {}),
                    ...itemDiff
                };
            }

            if (itemDiff !== undefined && typeof item !== 'object') return item;

            return undefined;
        }).filter(item => item !== undefined);

        return (changedElements.length > 0 || current.length < original.length)
            ? changedElements
            : undefined;
    }

    if (typeof current === 'object' && typeof original === 'object') {
        const changes = {};
        const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
        let hasChanges = false;

        keys.forEach((key) => {
            if (ignoreSet && ignoreSet.has(key)) return;

            const childChange = getChangedFields(current[key], original[key], ignoreSet);
            if (childChange !== undefined) {
                changes[key] = childChange;
                hasChanges = true;
            }
        });
        return hasChanges ? changes : undefined;
    }

    return current !== original ? current : undefined;
};

/**
 * Compute the diff for a single section against its snapshot in `duplicateDataSet`.
 * Returns the changed-fields object for that section, or `null` if nothing changed.
 *
 * `ignoreFields` (Set | Array of strings) is applied in addition to
 * `DEFAULT_IGNORED_FIELDS` and any entry in `SECTION_IGNORED_FIELDS[sectionKey]`.
 */
const getSectionChanges = (sectionKey, claimbeta, ignoreFields) => {
    const currentData = claimbeta?.[sectionKey];
    const originalData = claimbeta?.duplicateDataSet?.[sectionKey];

    if (!currentData || !originalData) return null;

    const mergedIgnore = new Set([
        ...DEFAULT_IGNORED_FIELDS,
        ...(SECTION_IGNORED_FIELDS[sectionKey] || []),
        ...(Array.isArray(ignoreFields) ? ignoreFields : []),
        ...(ignoreFields instanceof Set ? ignoreFields : []),
    ]);

    const changed = getChangedFields(currentData, originalData, mergedIgnore);
    if (!changed || (typeof changed === 'object' && Object.keys(changed).length === 0)) {
        return null;
    }
    return changed;
};

/**
 * Compute changed fields for every claim section in one pass.
 *
 * Output shape:
 *   {
 *     professionalClaimDetails: { ...changedFieldsOnly },
 *     billingInfoDetails:       { ...changedFieldsOnly },
 *     serviceLineDetails:       { ...changedFieldsOnly },
 *     additionalClaimDetails:   { ...changedFieldsOnly },
 *   }
 *
 * Sections without any change are omitted from the result.
 *
 * @param {object} claimbeta  Current redux slice (must contain `duplicateDataSet`).
 * @param {string|string[]} sectionKeys  Section key(s) to diff.
 * @param {string[] | Set<string> | Object<string, string[]>} [ignoreFields]
 *   Extra fields to skip while diffing. Pass an array/Set to apply globally,
 *   or an object `{ [sectionKey]: [...] }` to apply per-section.
 */
export const buildAllChangedFieldsPayload = (
    claimbeta,
    sectionKeys = CLAIM_SECTION_KEYS,
    ignoreFields
) => {
    const keys = Array.isArray(sectionKeys) ? sectionKeys : [sectionKeys];
    const isPerSectionMap =
        ignoreFields && !Array.isArray(ignoreFields) && !(ignoreFields instanceof Set)
        && typeof ignoreFields === 'object';

    let result = {};
    keys.forEach((key) => {
        const perSection = isPerSectionMap ? ignoreFields[key] : ignoreFields;
        const sectionChanges = getSectionChanges(key, claimbeta, perSection);
        if (sectionChanges) {
            result = sectionChanges;
        }else{
            result = {};
        }
    });
    return {[sectionKeys]:result};
};

/**
 * Normalizes raw adjustment data from the API into a flat, UI-friendly object.
 * 
 * Handles edge cases such as:
 * - Converting adjustment arrays (adj1, adj2) into single-value fields.
 * - Joining remark code arrays into a comma-separated string.
 * - Providing fallback "-" strings for null or undefined values to prevent UI breakage.
 * 
 * @param {Object} adjustments - The raw adjustment object from the server.
 * @returns {Object} A normalized object with camelCase keys and safe string fallbacks.
 */
const normalizeAdjustmentSummary = (adjustments = {}) => {
    const adj1 = Array.isArray(adjustments?.adj1) ? adjustments.adj1 : [];
    const adj2 = Array.isArray(adjustments?.adj2) ? adjustments.adj2 : [];
    
    const remarkCodes = Array.isArray(adjustments?.remark_codes)
        ? adjustments.remark_codes.join(", ")
        : adjustments?.remark_codes;
    return {
        adj1Code: adj1.map(a => a.code).join(", ") || "-",
        adj1Amount: adj1.map(a => a.amount ? `$${a.amount}` : "").filter(Boolean).join(", ") || "-",
        
        adj2Code: adj2.map(a => a.code).join(", ") || "-",
        adj2Amount: adj2.map(a => a.amount ? `$${a.amount}` : "").filter(Boolean).join(", ") || "-",
        
        copay: adjustments?.copay ?? "-",
        coInsurance: adjustments?.co_insurance ?? "-",
        deductible: adjustments?.deductible ?? "-",
        remarkCodes: remarkCodes || "-",
    };
};
/**
 * Normalizes a raw procedure object by mapping multiple possible API keys to a 
 * standard internal format.
 * * This is a defensive mapping strategy to handle inconsistent backend schemas 
 * or varying data sources (e.g., different clearinghouses). It ensures the 
 * UI components receive a predictable data structure regardless of the source.
 * * @param {Object} procedure - The raw procedure object from the API.
 * @returns {Object} A standardized procedure object with guaranteed fields and "-" fallbacks.
 */
export const normalizeProcedureRow = (procedure = {}) => ({
    code: procedure.procedure_code ?? "-",
        dos: procedure.dos ?? "-",
        description: procedure.description ?? "-",
        units: procedure.units ?? "-",
        charge: procedure.charge ?? "-",
        payment: procedure.payment ?? "-",
        adjustment: procedure?.adjustments?.adjustment_total ?? "-",
        balance: procedure?.status?.closing_balance ?? "-",
        adjustments: procedure?.adjustments ? normalizeAdjustmentSummary(procedure.adjustments) : null,
        summary: procedure?.status ? procedure.status : null, 
});


/**
* Hook for downloading and displaying activity ledger PDF
*/
export const claimStageIdsForEscalate = (professionalClaimDetails) => {
    const d = professionalClaimDetails;
    if (!d) return [];
    if (Array.isArray(d.claim_stage_ids) && d.claim_stage_ids.length) {
        return d.claim_stage_ids
            .map((id) => Number(id))
            .filter((n) => !Number.isNaN(n));
    }
    /** v2 claim custom response: claim_stage: [{ claim_stage_id, workflow_id, workflow_name }, ...] */
    if (Array.isArray(d.claim_stage) && d.claim_stage.length) {
        return d.claim_stage
            .map((row) => Number(row?.claim_stage_id ?? row?.id))
            .filter((n) => !Number.isNaN(n));
    }
    if (d.claim_stage != null && d.claim_stage !== "" && typeof d.claim_stage !== "object") {
        const n = Number(d.claim_stage);
        return Number.isNaN(n) ? [] : [n];
    }
    return [];
};

export const claimStagesForEscalateDropdown = (professionalClaimDetails) => {
    const d = professionalClaimDetails;
    if (!d || !Array.isArray(d.claim_stage)) return [];
    return d.claim_stage
        .map((row) => ({
            id: Number(row?.claim_stage_id ?? row?.id),
            label: row?.workflow_name ?? row?.name ?? "",
        }))
        .filter((x) => !Number.isNaN(x.id) && x.label);
};

export const reportsToDisplay = (professionalClaimDetails) => {
    const d = professionalClaimDetails;
    if (!d) return "";
    if (d.reports_to_name) return d.reports_to_name;

    const r = d.reports_to;
    if (!r) return "";
    if (typeof r === "string") return r;
    if (typeof r === "number") return String(r);

    return (
        r.full_name ||
        [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ") ||
        r.name ||
        r.user?.username ||
        r.user?.email ||
        ""
    );
};

/**
 * Processes additional claim details and resets ambulance-related fields 
 * if the ambulance claim status is set to "No" (2).
 * 
 * @param {Object} newClaimDetails - The incoming additionalClaimDetails payload
 * @param {Object} existingClaimDetails - The existing claim state (e.g., claimbeta.additionalClaimDetails)
 * @returns {Object} Processed additional claim object
 */
export const processAmbulanceClaim = (newClaimDetails, existingClaimDetails) => {
    let processedClaim = { ...(newClaimDetails || {}) };
    
    // Check current state of ambulance_claim (either newly changed in payload, or existing in state)
    const currentAmbulanceVal = processedClaim.ambulance_claim !== undefined 
        ? processedClaim.ambulance_claim 
        : existingClaimDetails?.ambulance_claim;

    // Reset fields to `null` if ambulance_claim is 2 (No)
    if (Number(currentAmbulanceVal) === 2) {
        processedClaim = {
            ...processedClaim,
            weight: null,
            distance: null,
            return_trip_reason: null,
            stretcher_reason: null,
            certification: null,
            transport_reason: null,
            transport_reason_id: null, 
            transport_code: null,
            condition_indicator: {
                patient_admitted_hospital: false, 
                patient_moved_by_stretcher: false, 
                patient_unconscious_shock: false,
                patient_transported_emergency: false, 
                patient_physically_restrained: false, 
                patient_visible_haemorrhaging: false,
                ambulance_service_medical_necessary: false, 
                patient_confined_bed_chair: false,
            },
            pickup_location_data: {
                street1: null, street2: null, city: null, state: null, 
                // Preserve country from either new payload or existing state
                country: processedClaim.pickup_location_data?.country || existingClaimDetails?.pickup_location_data?.country, 
                zip_code: null
            },
            dropoff_location_data: {
                street1: null, street2: null, city: null, state: null, 
                // Preserve country from either new payload or existing state
                country: processedClaim.dropoff_location_data?.country || existingClaimDetails?.dropoff_location_data?.country, 
                zip_code: null
            }
        };
    }   
    
    return processedClaim;
};

export const handleApiErrors = (status, data) => {
    // If there is no error status, return false to continue execution
    if (status >= 200 && status < 300) {
        return { hasError: false };
    } 

    // Extract the exact error string from the backend
    const rawError = data?.error || data?.message || data?.detail;

    // Handle 403 Permission Error
    if (status === 403) {
        return { hasError:true, msg: i18n.t('review_respond_popup.no_permission') };
    }

    // DYNAMIC ERROR HANDLING USING REGEX ---
    if (typeof rawError === 'string') {
        // Match: "too_many_files_uploaded_limit_is_{limit}"
        const maxFilesMatch = rawError.match(/^too_many_files_uploaded_limit_is_(\d+)$/);
        if (maxFilesMatch) {
            const limit = maxFilesMatch[1]; 
            return { 
                hasError: true, 
                msg: i18n.t('review_respond_popup.max_file_upload', { limit }) 
            };
        }

        // Match: "{filename}_too_many_files_uploaded_limit_is_{size}_MB"
        const fileSizeMatch = rawError.match(/^(.+?)_too_many_files_uploaded_limit_is_(\d+)_MB$/);
        if (fileSizeMatch) {
            const fileName = fileSizeMatch[1]; 
            const sizeLimit = fileSizeMatch[2]; 
            return { 
                hasError: true, 
                msg: i18n.t('review_respond_popup.file_size_exceeded', { fileName, sizeLimit }) 
            };
        }

        // Match: "file_should_be_following_format_{formats}"
        const formatMatch = rawError.match(/^file_should_be_following_format_(.+)$/);
        if (formatMatch) {
            const formats = formatMatch[1];
            return {
                hasError: true,
                msg: i18n.t('review_respond_popup.invalid_format', { formats })
            };
        }
    }

    // Map specific backend error strings to user-friendly messages
    const errorMapping = {
        "files_list_is_required_in_payload": i18n.t('review_respond_popup.valid_list_missing'),
        "file_name_is_required_for_each_file": i18n.t('review_respond_popup.file_name_missing'),
        "escalation_not_found_or_already_closed": i18n.t('review_respond_popup.escalation_closed'),
    };

    // If the exact error string matches our map, show that message
    if (errorMapping[rawError]) {
        return { hasError:true, msg: errorMapping[rawError] };
    }

    // Fallbacks for standard HTTP errors if string doesn't match
    if (status === 404) {
        return { hasError: true, msg: i18n.t('review_respond_popup.no_escalation')};
    }
    if (status === 400) {
        return { hasError: true, msg: i18n.t('review_respond_popup.invalid_req') || rawError };
    }

    // Generic fallback for unhandled errors
    return {
        hasError:true, 
        msg: i18n.t('review_respond_popup.failed_submit')
    }; 
};